import React, { useId, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { MAX_AI_FORM_ASSIST_MESSAGE_LENGTH } from "@api/shared-runtime/ai-form-assist";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface AIAssistantInputProps {
  onSend: (text: string) => void;
  isLoading: boolean;
  placeholder?: string;
  attachmentAction?: React.ReactNode;
}

export function AIAssistantInput({
  onSend,
  isLoading,
  placeholder = "Describe what you want to build...",
  attachmentAction,
}: AIAssistantInputProps) {
  const [value, setValue] = useState("");
  const messageLengthDescriptionId = useId();
  const messageLength = value.trim().length;
  const isMessageTooLong = messageLength > MAX_AI_FORM_ASSIST_MESSAGE_LENGTH;

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || isLoading || isMessageTooLong) {
      return;
    }
    onSend(trimmed);
    setValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="min-w-0 space-y-3">
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label="Message to AI assistant"
        aria-describedby={messageLengthDescriptionId}
        aria-invalid={isMessageTooLong}
        disabled={isLoading}
        rows={3}
        className="min-h-28 resize-y text-base leading-relaxed sm:text-base md:text-sm"
      />
      <div id={messageLengthDescriptionId} className="space-y-1 text-xs">
        <p className="text-slate-500">
          {messageLength.toLocaleString()} /{" "}
          {MAX_AI_FORM_ASSIST_MESSAGE_LENGTH.toLocaleString()} characters
        </p>
        {isMessageTooLong && (
          <p role="alert" className="text-red-700">
            Please shorten your message to{" "}
            {MAX_AI_FORM_ASSIST_MESSAGE_LENGTH.toLocaleString()} characters or
            fewer before sending.
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {attachmentAction}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!messageLength || isLoading || isMessageTooLong}
          className="ml-auto shrink-0 gap-2 px-4"
          aria-label="Send message"
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" aria-hidden="true" />
          ) : (
            <Send size={18} aria-hidden="true" />
          )}
          Send
        </Button>
      </div>
    </div>
  );
}
